import { BriefDownloadMenu } from "@features/AddCampaignBrief/ui/BriefDownloadMenu";
import { useEditorDownload } from "@features/AddCampaignBrief/lib/useEditorDownload";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { useEditorDocumentImport } from "@shared/ui/Editor/lib/useEditorDocumentImport";
import { DocumentImportOverlay } from "@shared/ui/Editor/ui/DocumentImportOverlay";
import { DocumentUploadButton } from "@shared/ui/Editor/ui/DocumentUploadButton";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import Editor from "@shared/ui/Editor/ui";
import { Loader2, X } from "lucide-react";
import { useEffect, useState, type RefObject } from "react";
import styles from "./AttachBriefEditor.module.scss";

interface AttachBriefEditorStepProps {
  editorRef: RefObject<EditorHandle | null>;
  editorKey: number;
  deliverableName: string;
  attachCount: number;
  editorTitle?: string;
  onContentChange: () => void;
  onMediaUploadStart: () => void;
  onMediaUploadComplete: () => void;
  onImportComplete?: () => void;
}

export function AttachBriefEditorStep({
  editorRef,
  editorKey,
  deliverableName,
  attachCount,
  editorTitle = "Creator brief",
  onContentChange,
  onMediaUploadStart,
  onMediaUploadComplete,
  onImportComplete,
}: AttachBriefEditorStepProps) {
  const { brief } = useBriefStore();
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusTone, setStatusTone] = useState<"ok" | "err">("ok");

  const showStatus = (message: string, tone: "ok" | "err" = "ok") => {
    setStatusTone(tone);
    setStatusMessage(message);
  };

  const { isDownloading, downloadLabel, handleDownloadDoc, handleDownloadPdf } =
    useEditorDownload({
      editorRef,
      campaignName: `${brief?.name || editorTitle} · ${deliverableName || editorTitle}`,
      showStatus,
      onMenuClose: () => undefined,
    });

  const documentImport = useEditorDocumentImport({
    editorRef,
    disabled: isDownloading,
    showStatus,
    onContentChange,
    onImportComplete,
  });

  const isBusy = isDownloading || documentImport.isImporting;

  useEffect(() => {
    if (!statusMessage || isBusy) return;
    const timer = setTimeout(() => setStatusMessage(null), 3200);
    return () => clearTimeout(timer);
  }, [statusMessage, isBusy]);

  return (
    <>
      <div className={styles.editorMeta}>
        <span className={styles.editorMetaLabel}>Deliverable:</span>
        <span className={styles.deliverablePill}>{deliverableName}</span>
        <span className={styles.metaDot}>·</span>
        <span>
          {attachCount} creator{attachCount === 1 ? "" : "s"}
        </span>
      </div>

      <section className={styles.editorCard}>
        <div className={styles.editorHeader}>
          <div>
            <h4 className={styles.editorTitle}>{editorTitle}</h4>
            <p className={styles.editorHint}>
              Press <kbd className={styles.kbd}>/</kbd> for blocks · upload DOC
              or PDF
            </p>
          </div>
          <div className={styles.editorHeaderActions}>
            <DocumentUploadButton
              fileInputRef={documentImport.fileInputRef}
              accept={documentImport.accept}
              disabled={isBusy}
              isImporting={documentImport.isImporting}
              onFileInputChange={documentImport.onFileInputChange}
              onClick={documentImport.openFilePicker}
            />
            <BriefDownloadMenu
              isDownloading={isDownloading}
              onDownloadDoc={handleDownloadDoc}
              onDownloadPdf={handleDownloadPdf}
            />
          </div>
        </div>

        {statusMessage ? (
          <div
            className={`${styles.statusBanner} ${
              statusTone === "err" ? styles.statusErr : styles.statusOk
            }`}
          >
            <span>{statusMessage}</span>
            <button
              type="button"
              onClick={() => setStatusMessage(null)}
              className={styles.dismissButton}
              aria-label="Dismiss"
            >
              <X size={12} />
            </button>
          </div>
        ) : null}

        <div
          className={`${styles.editorWrap} ${documentImport.isDragOver ? styles.editorWrapDrag : ""}`}
          onDragOverCapture={documentImport.onDragOver}
          onDragLeave={documentImport.onDragLeave}
          onDropCapture={documentImport.onDrop}
          onPasteCapture={documentImport.onPaste}
        >
          <DocumentImportOverlay
            isImporting={documentImport.isImporting}
            isDragOver={documentImport.isDragOver}
          />
          {isDownloading ? (
            <div className={styles.downloadOverlay}>
              <div className={styles.downloadPill}>
                <Loader2 size={15} className={styles.spinIcon} />
                {downloadLabel}
              </div>
            </div>
          ) : null}
          <Editor
            key={editorKey}
            ref={editorRef}
            initialHtml=""
            editable={!isDownloading}
            allowDocumentImport
            onChange={onContentChange}
            onMediaUploadStart={onMediaUploadStart}
            onMediaUploadComplete={onMediaUploadComplete}
          />
        </div>
      </section>
    </>
  );
}
